import { Alerts } from '@App/alerts'
import ExtensionContext from '@App/extensions/components/ExtensionContext'
import CloudinaryURLProvider from '@Components/ImageContainer/CloudinaryURLProvider'
import { withMobx } from '@Di'
import { getIso15924Tag } from '@Locales'
import loadLocaleData from '@Locales/node/loadLocaleData'
import {
  alertsAtom,
  cloudinaryUrlAtom,
  currentPostDetailAtom,
  hubAtom,
  orgadminOrgsAtom,
  orgadminUsersAtom,
  restApiAtom,
  rpcApiAtom,
  sessionAtom,
} from '@Modules/hubs/atoms'
import {
  composerRootPost,
  newsletterRecipientsAtom,
  newsletterResponseAtom,
  newsletterSubjectAtom,
} from '@Modules/hubs/newsletters/atoms'
import { HubsAPI } from '@RestApi/api'
import RestApiClient from '@RestApi/client'
import { fetchWithAuthentication } from '@RestApi/fetch'
import * as Sentry from '@sentry/node'
import * as getStores from '@Stores'
import { theme } from '@Styles/config'
import GlobalStyle from '@Styles/global-styles'
import getMessages from '@Translations'
import 'draft-js/dist/Draft.css'
import { configure } from 'mobx'
import { observer, Provider, useStaticRendering } from 'mobx-react'
import React, { Component } from 'react'
import 'react-dates/lib/css/_datepicker.css'
import { Helmet } from 'react-helmet'
import { IntlProvider } from 'react-intl'
import { RecoilRoot, useSetRecoilState } from 'recoil'
import { ThemeProvider } from 'styled-components'
import {
  collectionIdsAtom,
  archivedCollectionIdsAtom,
  collectionDataAtom,
} from '@Modules/settings/collections/atoms'
import { isRTL } from 'src/common/rtl/rtlTransform'

const isServer = !process.browser
configure({ enforceActions: 'observed' })
useStaticRendering(isServer) // NOT `true` value

if (process.env.NODE_ENV === 'development' && process.env.ENABLE_MOBX_LOGGER === 'true') {
  const { enableLogging } = require('mobx-logger')
  enableLogging()
}

if (process.env.SENTRY_PUBLIC_DSN) {
  Sentry.init({
    enabled: process.env.NODE_ENV === 'production',
    dsn: process.env.SENTRY_PUBLIC_DSN,
  })
}

/**
 * Custom app: https://nextjs.org/docs/advanced-features/custom-app
 *
 * TODO: Rewrite this to TypeScript. See https://nextjs.org/docs/basic-features/typescript#custom-app.
 */
@withMobx(getStores)
@observer
class App extends Component {
  // TODO: Rewrite this to getServerSideProps(), so this don't get called on every single page link click.
  static async getInitialProps({ Component, ctx }) {
    // Get the `locale` and `messages` from the request object on the server.
    // In the browser, use the same values that the server serialized.
    const { req, query } = ctx
    const { messages } = req || window?.__NEXT_DATA__?.props

    // The "NEW CODE".
    const isServer = typeof window === 'undefined'

    const restApiSettings = !isServer
      ? window?.__NEXT_DATA__?.props?.restApiSettings
      : {
          ssrURL: req.ssrURL || 'http://localhost:5331',
          clientURL: req.clientURL || 'http://localhost:5331',
          jwt: req.jwt,
          csrf: req.csrf,
          VERSION: 'vXXX',
        }

    const { ssrURL, clientURL, jwt, csrf, VERSION } = restApiSettings

    // Initialize REST API client, so we can fetch the initial data.
    const restApi = new RestApiClient(
      isServer ? ssrURL : clientURL,
      jwt,
      csrf,
      VERSION,
      process.env.NODE_ENV === 'development'
    )

    // Initialize RPC REST API client, so we can fetch the initial data.
    const rpcApi = new HubsAPI(
      isServer ? ssrURL : clientURL,
      fetchWithAuthentication('next', VERSION, jwt, csrf)
    )

    let initState = !isServer ? window?.__NEXT_DATA__?.props?.initState : {}

    if (isServer) {
      // Fetch user's session.
      initState = {
        ...initState,
        session: await restApi.GetSession(),
        cloudinaryUrl: process.env.CLOUDINARY_URL,
      }

      // Fetch hub.
      let queryHub = query.hubPrefix || ''
      if (queryHub.startsWith('hub-')) {
        // Trim hub- prefix.
        const hubId = queryHub.slice(4)
        initState = {
          ...initState,
          hubResponse: await restApi.GetHubById(hubId),
        }
      } else if (req?.hubId) {
        initState = {
          ...initState,
          hubResponse: await restApi.GetHubById(req.hubId),
        }
      }
    }

    // Call getInitialProps() of the current Page Component in /pages/*, pass down some useful props.
    if (Component.getInitialProps) {
      initState = {
        ...initState,
        ...(await Component.getInitialProps({
          ...ctx,
          isServer,
          initState,
          restApi,
          rpcApi,
        })),
      }
    }

    // "OLD CODE" - init MOBX stores.
    ctx.store.SessionStore.setSession(initState.session)

    let currentMessages = {}
    let currentLocale = 'en'
    let langDirection = 'LTR'

    if (initState.hubResponse?.data?.locale) {
      await loadLocaleData(initState.hubResponse.data.locale)
      currentMessages = await getMessages(initState.hubResponse.data.locale)
      currentLocale = initState.hubResponse.data.locale

      if (isRTL(currentLocale)) langDirection = 'RTL'
    }

    return {
      messages,
      restApiSettings,
      initState,
      currentMessages,
      langDirection,
      currentLocale,
    }
  }

  serializeSession = () => {
    const { store } = this.props
    const session = store.SessionStore.getSession()

    if (!session) return null

    return {
      id: session.id,
      email: session.email,
      name: session.name,
      avatar_url: session.avatar_url,
      cover_url: session.cover_url,
    }
  }

  render() {
    const {
      Component,
      store,
      initState,
      messages,
      langDirection,

      currentMessages,
      currentLocale,
    } = this.props
    const { ssrURL, clientURL, jwt, csrf, VERSION } = this.props.restApiSettings
    const isServer = typeof window === 'undefined'

    // Initialize REST API client.
    // Note: We can't re-use the restApi object from getInitialProps(), as it won't work in browser.
    const restApi = new RestApiClient(
      isServer ? ssrURL : clientURL,
      jwt,
      csrf,
      VERSION,
      process.env.NODE_ENV === 'development'
    )

    // Initialize RPC REST API client.
    const rpcApi = new HubsAPI(
      isServer ? ssrURL : clientURL,
      fetchWithAuthentication('next', VERSION, jwt, csrf)
    )

    const initializeState = ({ set }) => {
      set(restApiAtom, restApi)
      set(rpcApiAtom, rpcApi)

      set(cloudinaryUrlAtom, initState.cloudinaryUrl)

      set(sessionAtom, initState.session)
      set(hubAtom, initState.hubResponse)
      set(alertsAtom, initState.alerts || [])
      set(currentPostDetailAtom, initState.currentPostDetail)
      set(orgadminOrgsAtom, initState.orgadminOrgs || [])
      set(orgadminUsersAtom, initState.orgadminUsers || [])

      if (initState.collectionData && initState.collectionData.length) {
        const activeCollections = initState.collectionData.filter(
          (collection) => !collection.archived
        )
        const archivedCollections = initState.collectionData.filter(
          (collection) => collection.archived
        )

        const collectionIds = activeCollections.map((collection) => collection.id)
        const archivedCollectionIds = archivedCollections.map((collection) => collection.id)
        set(collectionIdsAtom, collectionIds)
        set(archivedCollectionIdsAtom, archivedCollectionIds)

        initState.collectionData.forEach((collection) => {
          set(collectionDataAtom(collection.id), collection)
        })
      }

      if (initState.newsletterResponse) {
        set(newsletterResponseAtom, initState.newsletterResponse)

        if (initState.newsletterResponse.data) {
          set(newsletterSubjectAtom, initState.newsletterResponse.data.subject || '')
          set(composerRootPost, initState.newsletterResponse.data.post)

          const recipientsArray = []
          initState.newsletterResponse.data.roles &&
            initState.newsletterResponse.data.roles.map((role) =>
              recipientsArray.push({
                itemId: `${role.charAt(0).toUpperCase() + role.slice(1)}s`,
                count: initState.hubResponse.data[`${role}_count`],
                display: true,
                source: role,
                type: 'HubMembers',
              })
            )
          initState.newsletterResponse.data.sparq3_filter_ids &&
            initState.newsletterResponse.data.sparq3_filter_ids.map((filter) =>
              recipientsArray.push({
                itemId: null,
                count: null,
                display: true,
                source: filter,
                type: 'Filter',
              })
            )

          initState.newsletterResponse.data.sparq3_dynamic_filters &&
            initState.newsletterResponse.data.sparq3_dynamic_filters.map((filter) =>
              recipientsArray.push({
                itemId: null,
                count: null,
                display: true,
                source: filter.id,
                acceptedValues: filter.type === 'survey' ? filter.values : null,
                type: filter.type,
              })
            )

          set(newsletterRecipientsAtom, recipientsArray)

          // NOTE: Composer post blocks are initialized in the useEffect() in the Newsletter Composer.
        }
      }
    }

    return (
      <RecoilRoot initializeState={initializeState}>
        <Provider {...store}>
          <ThemeProvider theme={theme}>
            <GlobalStyle />
            <IntlProvider
              defaultLocale="en"
              key={getIso15924Tag(currentLocale)}
              locale={getIso15924Tag(currentLocale)}
              messages={currentMessages}
              onError={(err) => {
                if (err.code === 'MISSING_TRANSLATION') {
                  return
                }
                throw err
              }}
            >
              <CloudinaryURLProvider>
                <ExtensionContext name="root" data={{ session: this.serializeSession() }}>
                  <FunctionalWrapper
                    Component={Component}
                    restApi={restApi}
                    initState={initState}
                    langDirection={langDirection}
                    err={initState.err}
                  />
                </ExtensionContext>
              </CloudinaryURLProvider>
            </IntlProvider>
          </ThemeProvider>
        </Provider>
      </RecoilRoot>
    )
  }
}

// A handy wrapper for Page Component where you can use hooks.
const FunctionalWrapper = ({ Component, initState, restApi, langDirection, err }) => {
  const setAlerts = useSetRecoilState(alertsAtom)

  const dismissAlert = (id) => {
    setAlerts((oldAlerts) => {
      return oldAlerts.filter((item) => item.id !== id)
    })
  }

  return (
    <>
      <Helmet
        defer={false}
        htmlAttributes={{
          lang: 'en',
          dir: langDirection,
        }}
        meta={[
          {
            name: 'theme-color',
            content: 'header color from Theme Store',
          },
          {
            name: 'viewport',
            content: 'width=device-width, minimum-scale=1, maximum-scale=5, initial-scale=1',
          },
        ]}
      />
      <Alerts dismiss={dismissAlert} dir={langDirection} />
      <Component initState={initState} restApi={restApi} err={err} />
    </>
  )
}

export default App
